import request from 'superagent';
import { from } from 'rxjs';

const URL = process.env.REACT_APP_API_URL;
const baseURL = `${URL}/api/rest`;

export function apiCall(method, path, data, requestOptions = req => req) {
  const req = request(method, baseURL + path).withCredentials();
  requestOptions(req);
  if (data) {
    req.send(data);
  }

  // Set bearer token, if user is logged in
  const token = localStorage.getItem('accessToken');
  if (token) {
    req.set('Authorization', `Bearer ${token}`);
  }

  return from(req);
}

const api = {
  auth: {
    login: (username, password, otp) => {
      const req = request.post(`${URL}/auth/token`).send({
        username,
        password,
        otp,
      });
      req.catch(x => console.log(req, x)); // eslint-disable-line
      req.then(() => {});
      return from(req);
    },

    sso: () => apiCall('get', '/users/me/token'),

    fetchMyUser: () => apiCall('get', '/users/me'),

    forgotPassword: data => apiCall('post', '/users/forgot-password', data),

    verifyResetToken: token => apiCall('get', `/users/verify-reset-token?token=${token}`),

    verifyResetAuthCode: (authCode, token) =>
      apiCall('get', `/users/verify-reset-auth-code?authCode=${authCode}&token=${token}`),

    resetPassword: data => apiCall('post', '/users/reset-password', data),

    changePassword: (oldPassword, newPassword) =>
      apiCall('post', '/users/change-password', { oldPassword, newPassword }),

    init2Fa: () => apiCall('post', '/auth/init2fa'),

    enable2Fa: (otp, secret) => apiCall('post', '/auth/enable2fa', { otp, secret }),

    verifyPassword: password => apiCall('post', '/auth/verify-password', { password }),

    logout: () => apiCall('post', '/auth/logout', null),
  },

  spaces: {
    fetchAll: () => apiCall('get', '/spaces'),

    create: space => apiCall('post', '/spaces', space),

    update: space => apiCall('put', `/spaces/${space.id}`, space),

    updateBody: (id, body) => apiCall('put', `/spaces/${id}/body`, body),

    delete: space => apiCall('delete', `/spaces/${space.id}`),

    downloadIndex: space => apiCall('get', `/spaces/${space.id}/index`),

    uploadInlineContentImage: (space, file) =>
      apiCall('post', `/spaces/${space.id}/image`, null, req => {
        req.attach('image', file);
      }),
  },

  tags: {
    fetchAll: () => apiCall('get', '/tags'),

    create: tag => apiCall('post', '/tags', tag),

    update: tag => apiCall('put', `/tags/${tag.id}`, tag),

    delete: tag => apiCall('delete', `/tags/${tag.id}`),
  },

  mailingLists: {
    fetchAll: () => apiCall('get', '/mailing-lists'),

    create: mailingList => apiCall('post', '/mailing-lists', mailingList),

    update: mailingList => apiCall('put', `/mailing-lists/${mailingList.id}`, mailingList),

    delete: mailingList => apiCall('delete', `/mailing-lists/${mailingList.id}`),
  },

  contacts: {
    fetchAll: () => apiCall('get', '/contacts'),

    fetchOneByUserId: userId => apiCall('get', `/users/${userId}/contact`),

    downloadVcard: contact => apiCall('get', `/contacts/download/${contact.id}`, null, req => req.responseType('blob')),

    fetchAllBySpaceId: spaceId => apiCall('get', `/contacts?spaceId=${spaceId}`),

    create: contact =>
      apiCall('post', '/contacts', null, req => {
        const contactJson = new Blob([JSON.stringify({ ...contact })], { type: 'application/json' });
        req.attach('contact', contactJson);
        if (contact.imageFile.length > 0) {
          req.attach('image', contact.imageFile[0].fileObject);
        }
      }),

    update: contact =>
      apiCall('put', `/contacts/${contact.id}`, null, req => {
        const contactJson = new Blob([JSON.stringify({ ...contact })], { type: 'application/json' });
        req.attach('contact', contactJson);
        if (contact.imageFile.length > 0) {
          req.attach('image', contact.imageFile[0].fileObject);
        }
      }),

    delete: contact => apiCall('delete', `/contacts/${contact.id}`),

    importFromCsv: csv => {
      const attachFiles = req => {
        req.attach('csv', csv);
      };
      return apiCall('post', '/contacts/import', null, attachFiles);
    },

    fetchMyContact: () => apiCall('get', '/me/contact'),

    // updateMyContact: contact => apiCall('put', '/me/contact', contact),

    updateMyContact: contact =>
      apiCall('put', '/me/contact', null, req => {
        const contactJson = new Blob([JSON.stringify({ ...contact })], { type: 'application/json' });
        req.attach('contact', contactJson);
        if (contact.imageFile.length > 0) {
          req.attach('image', contact.imageFile[0].fileObject);
        }
      }),
  },

  folders: {
    fetchTreeBySpaceId: spaceId => apiCall('get', `/folders?spaceId=${spaceId}&anyParent=true`),

    fetchBySpaceId: spaceId => apiCall('get', `/folders?spaceId=${spaceId}&anyParent=true`),

    fetchChildren: (spaceId, parentId) => {
      const attachQueryParams = req => {
        req.query({ spaceId });
        if (parentId) {
          req.query({ parentId });
        }
      };
      return apiCall('get', '/folders', null, attachQueryParams);
    },

    fetchFolderDocumentCountBySpaceId: spaceId => apiCall('get', `/folders/document-count?spaceId=${spaceId}`),

    create: folder => apiCall('post', '/folders', folder),

    createMultiple: multipleFolders => apiCall('post', '/folders/multiple', multipleFolders),

    update: folder => apiCall('put', `/folders/${folder.id}`, folder),

    updateMultiple: folders => apiCall('put', '/folders', folders),

    delete: folder => apiCall('delete', `/folders/${folder.id}`),

    uploadInlineContentImage: (folder, file) =>
      apiCall('post', `/folders/${folder.id}/image`, null, req => {
        req.attach('image', file);
      }),
  },

  documents: {
    fetchAllByActiveFolder: (spaceId, folderId) => {
      const attachQueryParams = req => {
        req.query({ spaceId });
        if (folderId) {
          req.query({ folderId });
        }
      };
      return apiCall('get', '/documents', null, attachQueryParams);
    },

    fetchAllRecursiveByFolderPath: (spaceId, folderPath) =>
      apiCall('get', `/documents?spaceId=${spaceId}&folderPath=${folderPath || ''}`),

    fetch: id => apiCall('get', `/documents/${id}`),

    fetchAnnotations: (documentId, password) =>
      apiCall('get', `/documents/${documentId}/pdf-version/annotations?password=${password}`),

    updateAnnotations: (documentId, annotations, password = null) =>
      apiCall('put', `/documents/${documentId}/pdf-version/annotations`, { annotations, password }),

    create: document => apiCall('post', '/documents', document),

    uploadMultiple: ({ fileObjects, names, spaceId, folderId, subFolders = null, progressCallback = () => {} }) => {
      const attachFiles = req => {
        fileObjects.forEach(file => req.attach('files', file));
        names.forEach(name => req.field('names', name));
        if (subFolders) {
          subFolders.forEach(subFolder => req.field('subFolders', subFolder));
        }
        req.field('spaceId', spaceId);
        if (folderId) {
          req.field('folderId', folderId);
        }
        req.on('progress', event => {
          progressCallback(event);
        });
      };
      return apiCall('post', '/documents/multiple', null, attachFiles);
    },

    clone: id => apiCall('post', `/documents/${id}/clone`, null),

    download: document => apiCall('get', `/documents/${document.id}/download`, null, req => req.responseType('blob')),

    downloadPdfVersion: document =>
      apiCall('get', `/documents/${document.id}/pdf-version/download`, null, req => req.responseType('blob')),

    downloadMultiple: (documents = [], folders = []) =>
      apiCall('put', '/documents/download/multiple', { documents, folders }, req => req.responseType('blob')),

    update: document => apiCall('put', `/documents/${document.id}`, document),

    replaceFile: (document, newFile, password) =>
      apiCall('put', `/documents/${document.id}/file`, null, req => {
        req.attach('newFile', newFile.fileObject);
        if (password) {
          req.field('password', password);
        }
      }),

    delete: document => apiCall('delete', `/documents/${document.id}`),

    share: (documentIds, folderIds, userIds, roleIds, mailingListIds, ccUserIds, subject, message) =>
      apiCall('post', '/documents/share/', {
        documentIds,
        folderIds,
        userIds,
        roleIds,
        mailingListIds,
        ccUserIds,
        subject,
        message,
      }),

    getAuthorizedRoles: (documentIds, folderIds) => {
      const attachQueryParams = req => {
        if (documentIds) {
          req.query({ documentIds });
        }
        if (folderIds) {
          req.query({ folderIds });
        }
      };
      return apiCall('get', '/documents/share/roles', null, attachQueryParams);
    },

    getAuthorizedUsers: (documentIds, folderIds) => {
      const attachQueryParams = req => {
        if (documentIds) {
          req.query({ documentIds });
        }
        if (folderIds) {
          req.query({ folderIds });
        }
      };
      return apiCall('get', '/documents/share/users', null, attachQueryParams);
    },

    getAuthorizedMailingLists: (documentIds, folderIds) => {
      const attachQueryParams = req => {
        if (documentIds) {
          req.query({ documentIds });
        }
        if (folderIds) {
          req.query({ folderIds });
        }
      };
      return apiCall('get', '/documents/share/mailing-lists', null, attachQueryParams);
    },
  },

  roles: {
    fetchAll: () => apiCall('get', '/roles'),

    fetchAllBySpaceId: spaceId => apiCall('get', `/spaces/${spaceId}/roles`),

    create: role => apiCall('post', '/roles', role),

    update: role => apiCall('put', `/roles/${role.id}`, role),

    delete: role => apiCall('delete', `/roles/${role.id}`),
  },

  users: {
    fetchAllAuthors: () => apiCall('get', '/authors'),

    fetchAll: () => apiCall('get', '/users'),

    fetchAllBySpaceId: spaceId => apiCall('get', `/spaces/${spaceId}/members`),

    fetch: id => apiCall('get', `/users/${id}`),

    create: user => apiCall('post', '/users', user),

    update: user => apiCall('put', `/users/${user.id}`, user),

    delete: user => apiCall('delete', `/users/${user.id}`),
  },

  feeds: {
    fetchBySpaceId: spaceId => (spaceId ? apiCall('get', `/feed?spaceId=${spaceId}`) : apiCall('get', '/feed')),

    fetchDocumentsBySpaceId: spaceId =>
      spaceId ? apiCall('get', `/documents/feed?spaceId=${spaceId}`) : apiCall('get', '/documents/feed'),
  },

  myFavorites: {
    fetchAll: () => apiCall('get', '/me/favorites'),

    addFavorite: (entityType, entityId) => apiCall('post', `/me/favorites/${entityType}/${entityId}`),

    removeFavorite: (entityType, entityId) => apiCall('delete', `/me/favorites/${entityType}/${entityId}`),
  },

  defaultFavorites: {
    fetchAll: dashboardId => apiCall('get', `/dashboards/${dashboardId}/favorites`),

    fetchAllDashboardsWithFavoriteDocument: (entityType, entityId) =>
      apiCall('get', `/dashboards/favorites/${entityType}/${entityId}`),

    addFavorite: (dashboardId, entityType, entityId) =>
      apiCall('post', `/dashboards/${dashboardId}/favorites/${entityType}/${entityId}`),

    removeFavorite: (dashboardId, entityType, entityId) =>
      apiCall('delete', `/dashboards/${dashboardId}/favorites/${entityType}/${entityId}`),
  },

  myDashboards: {
    fetch: () => apiCall('get', '/me/dashboard'),

    create: dashboard => apiCall('post', '/me/dashboard', dashboard),

    update: dashboard => apiCall('put', `/me/dashboard/${dashboard.id}`, dashboard),
  },

  defaultDashboards: {
    fetchAll: () => apiCall('get', '/dashboards?defaultOnly=true'),

    create: dashboard => apiCall('post', '/dashboards', dashboard),

    update: dashboard => apiCall('put', `/dashboards/${dashboard.id}`, dashboard),

    delete: dashboard => apiCall('delete', `/dashboards/${dashboard.id}`),
  },

  permissions: {
    fetchAllAclSids: () => apiCall('get', '/acls/sids'),

    fetchAllAclObjectIdentities: () => apiCall('get', '/acls/objectidentities'),

    fetchAllAclEntityIdToOidIndex: () => apiCall('get', '/acls/entity-oid-index'),

    fetchPermissionsBySid: sid => apiCall('get', `/acls/permissions?sid=${sid}`),

    update: permission => apiCall('post', '/acls/permissions', permission),

    delete: (objectIdentityId, sid) =>
      apiCall('delete', `/acls/permissions?sid=${sid}&objectIdentityId=${objectIdentityId}`),
  },

  globalPermissions: {
    fetchAll: () => apiCall('get', '/permissions'),

    update: permission => apiCall('put', `/permissions/${permission.id}`, permission),
  },

  audit: {
    fetchPendingRoles: spaceId => apiCall('get', `/audit/spaces/${spaceId}/roles/pending`),

    confirmUserAudit: spaceId => apiCall('post', `/audit/spaces/${spaceId}/users/confirm`, {}),

    confirmRoleAudit: (spaceId, role) => apiCall('post', `/audit/spaces/${spaceId}/roles/confirm`, role),
  },

  news: {
    fetch: () => apiCall('get', '/news'),
  },

  search: {
    search: (key, queryParams = {}) => {
      const attachQueryParams = req => {
        req.query({ key });
        if (queryParams) {
          Object.entries(queryParams).forEach(param => {
            if (param && param.length === 2 && param[1] !== undefined && param[1] !== null) {
              req.query({ [param[0]]: param[1] });
            }
          });
        }
      };
      return apiCall('get', '/search', null, attachQueryParams);
    },
  },

  trash: {
    fetchDeletedDocuments: spaceId => apiCall('get', `/documents/?spaceId=${spaceId}&isDeleted=true`),

    fetchDeletedFolders: spaceId => apiCall('get', `/folders/?spaceId=${spaceId}&isDeleted=true`),

    restoreDocuments: documents => apiCall('put', '/documents/restore', documents),
  },
};

export default api;
